import { ClipboardCheck } from "lucide-react";
import { PROVINCES, EDUCATION_OPTIONS, TEER_OPTIONS, FRENCH_OPTIONS, LEGAL_STATUS_OPTIONS, REGIONS_BY_PROVINCE, TEST_LABELS, FRENCH_TEST_LABELS, CONNECTION_LABELS } from "@/lib/constants";
import { SectionTitle } from "@/components/assessment/fields";

const spouseLangSummary = (sp) => {
  if (sp.testType && sp.testType !== "clb") {
    const l = sp.language || {};
    return `${TEST_LABELS[sp.testType]} · L ${l.listening || "—"} / R ${l.reading || "—"} / W ${l.writing || "—"} / S ${l.speaking || "—"}`;
  }
  return `Approx. CLB ${sp.clb || 0}`;
};

const frenchSummary = (profile) => {
  if (profile.frenchTest && profile.frenchTest !== "clb") {
    const f = profile.french || {};
    return `${FRENCH_TEST_LABELS[profile.frenchTest]} · L ${f.listening || "—"} / R ${f.reading || "—"} / W ${f.writing || "—"} / S ${f.speaking || "—"}`;
  }
  return FRENCH_OPTIONS.find((o) => String(o.value) === String(profile.frenchCLB))?.label;
};


const optionLabel = (options, value) => options.find((o) => String(o.value) === String(value))?.label;
const connectionSummary = (profile) =>
  (profile.provincialConnection || [])
    .map((c) => `${c} (${[].concat(profile.connectionTypes?.[c] || []).map((t) => CONNECTION_LABELS[t]).filter(Boolean).join(", ") || "connection"})`)
    .join("; ") || "None";

function personalRows(profile) {
  const spouseOn = profile.maritalStatus === "married" && profile.spouseAccompanying;
  return [
    ["Age", profile.age, "age"],
    ["Marital status", profile.maritalStatus === "married" ? "Married / common-law" : "Single", "spouse"],
    spouseOn && ["Spouse language", spouseLangSummary(profile.spouse || {}), "spouse"],
    ["Education", optionLabel(EDUCATION_OPTIONS, profile.education), "education"],
    profile.eduInCanada && ["Canadian education completed in", PROVINCES.find((p) => p.code === profile.eduProvince)?.name || "Province not set", "eduInCanada"],
  ];
}

function languageRows(profile) {
  return [
    ["English test", TEST_LABELS[profile.testType] || profile.testType.toUpperCase(), "language"],
    ["Language scores", ["listening", "reading", "writing", "speaking"].map((a) => profile.language?.[a] || "—").join(" / "), "language"],
    ["French", frenchSummary(profile), "french"],
  ];
}

function workRows(profile) {
  return [
    profile.tradeCertificate && ["Trade certificate", "Canadian certificate of qualification", "experience"],
    ["Foreign experience", `${profile.foreignExp} yr`, "experience"],
    ["Canadian experience", `${profile.canadianExp} yr`, "experience"],
    ["Occupation title", profile.occupationTitle ? `${profile.occupationTitle}${profile.nocCode ? ` (NOC ${profile.nocCode})` : ""}` : "—", null],
    ["Occupation TEER", optionLabel(TEER_OPTIONS, profile.noc_teer), "teer"],
  ];
}

function connectionRows(profile, streamProvince) {
  const regions = profile.jobOffer && REGIONS_BY_PROVINCE[profile.jobOfferProvince || streamProvince];
  return [
    ["Provincial connections", connectionSummary(profile), "connection"],
    ["Job offer", profile.jobOffer ? `Yes · ${profile.jobOfferProvince || "—"}` : "No", "jobOffer"],
    profile.jobOffer && ["Time with this employer", `${profile.jobOfferMonths || 0} month(s) full-time`, "jobOfferDetails"],
    regions && ["Wage & job location", `$${profile.jobOfferWage || 0}/hr · ${regions.find((o) => o.value === (profile.jobOfferRegion || ""))?.label || "region not set"}`, "jobOfferDetails"],
    ["Status in Canada", optionLabel(LEGAL_STATUS_OPTIONS, profile.legalStatus || "none"), "legalStatus"],
    ["Settlement funds", `$${Number(profile.funds).toLocaleString()}`, "funds"],
  ];
}

function reviewRows(profile, show, streamProvince) {
  return [...personalRows(profile), ...languageRows(profile), ...workRows(profile), ...connectionRows(profile, streamProvince)]
    .filter(Boolean)
    .filter(([, , key]) => !key || show(key));
}


export function ReviewStep({ profile, show, streamName, streamProvince }) {
  const rows = reviewRows(profile, show, streamProvince);
  return (
    <div className="space-y-6">
      <SectionTitle icon={ClipboardCheck} title={streamName ? `Review — ${streamName}` : "Review your profile"} />
      <p className="text-sm text-slate-600">
        {streamName ? "Confirm the details this stream scores, then calculate." : "Confirm your details below, then calculate your results."} You can jump back to any step to edit.
      </p>
      <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="review-list">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 px-4 py-3">
            <dt className="text-sm font-medium text-slate-500">{k}</dt>
            <dd className="text-right text-sm font-semibold text-slate-900">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
